import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  ActionButton,
  CatalogNavTree,
  EmptyGridState,
  SectionCard,
} from "@/app/admin/system-catalog/systemCatalogAdminUi";
import AdminLoading from "@/app/admin/AdminLoading";
import { useToast } from "@/components/ui/ToastProvider";
import { useAdminCreateWorkspaceQuery } from "@/lib/queries/adminQueries";
import { apiFetch } from "@/lib/apiClient";
import {
  buildSystemCatalogDerivedData,
  getCollectionTileSets,
  getOrphanTileSets,
  getRootCollections,
  getTileRecordPreviewUrl,
  type SystemCatalogSelection,
} from "@/app/admin/system-catalog/systemCatalogAdminModel";
import type { SystemCatalogDragPayload } from "@/app/admin/system-catalog/systemCatalogAdminTypes";
import type { TaxonomyNodeRecord, TileRecord } from "@west-marches/shared";
import type {
  AdminCreateOverlayAsset,
  AdminCreateCreatedTile,
  AdminCreateWorkspaceData,
} from "@/app/admin/adminTypes";

import {
  ADMIN_CREATE_CANVAS_HEIGHT,
  ADMIN_CREATE_CANVAS_WIDTH,
  clampAdminCreateScale,
  createAdminCreateLayer,
  duplicateAdminCreateLayer,
  exportAdminCreateCompositionToDataUrl,
  moveLayerBackward,
  moveLayerForward,
  prepareAdminCreateComposeTransition,
  type AdminCreateLayer,
} from "./createEditor";

interface AdminCreateWorkspaceProps {
  initialData?: AdminCreateWorkspaceData;
}

function reportAdminCreateError(action: string, error: unknown, extras?: Record<string, unknown>) {
  Sentry.captureException(error instanceof Error ? error : new Error(action), {
    tags: {
      area: "admin-create",
      action,
    },
    extra: extras,
  });
}

export function AdminCreateWorkspace({ initialData }: AdminCreateWorkspaceProps) {
  const { data: workspace, isLoading } = useAdminCreateWorkspaceQuery(initialData);

  if (isLoading || !workspace) {
    return <AdminLoading />;
  }

  return <AdminCreateWorkspaceLoaded workspace={workspace} />;
}

type DragState =
  | {
      kind: "move";
      layerId: string;
      startClientX: number;
      startClientY: number;
      originX: number;
      originY: number;
    }
  | {
      kind: "scale";
      layerId: string;
      originScale: number;
      centerClientX: number;
      centerClientY: number;
      startDistance: number;
    };

type BrowseMode = "tree" | "orphans" | "collection";

function stripPngExtension(filename: string): string {
  return filename.replace(/\.png$/i, "");
}

const noopDropOnTaxonomy = (_targetId: string, _payload: SystemCatalogDragPayload) => {};
const noopDropOnTileSet = (_targetTileSetId: string, _payload: SystemCatalogDragPayload) => {};

function AdminCreateWorkspaceLoaded({ workspace: initialWorkspace }: { workspace: AdminCreateWorkspaceData }) {
  const toast = useToast();
  const {
    data: workspaceQueryData,
    error: workspaceError,
    refetch: refetchWorkspace,
  } = useAdminCreateWorkspaceQuery(initialWorkspace);
  const [workspaceOverride, setWorkspaceOverride] = useState<AdminCreateWorkspaceData | null>(null);
  const workspace = workspaceOverride ?? workspaceQueryData ?? initialWorkspace;
  const [baseTileId, setBaseTileId] = useState(initialWorkspace.baseTiles[0]?.id ?? null);
  const [layers, setLayers] = useState<AdminCreateLayer[]>([]);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const [selectedOverlayQuantities, setSelectedOverlayQuantities] = useState<Record<string, number>>({});
  const [overlaySearch, setOverlaySearch] = useState("");
  const [filename, setFilename] = useState("Created Tile");
  const [isSaving, setIsSaving] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragState | null>(null);

  const [catalogSelection, setCatalogSelection] = useState<SystemCatalogSelection>({ kind: "root" });
  const [expandedTreeKeys, setExpandedTreeKeys] = useState<Set<string>>(() => new Set());
  const [activeTileSetId, setActiveTileSetId] = useState<string | null>(null);
  const [browseMode, setBrowseMode] = useState<BrowseMode>("tree");
  const [browseCollectionId, setBrowseCollectionId] = useState<string | null>(null);

  const [hoverPreview, setHoverPreview] = useState<{
    url: string;
    filename: string;
    clientX: number;
    clientY: number;
  } | null>(null);

  const setStatus = useCallback(
    (message: string | null) => {
      if (message) {
        toast.info(message);
      }
    },
    [toast],
  );

  const setError = useCallback(
    (message: string | null) => {
      if (message) {
        toast.error(message);
      }
    },
    [toast],
  );

  useEffect(() => {
    if (!workspaceError) {
      return;
    }

    toast.error(
      workspaceError instanceof Error ? workspaceError.message : "Failed to refresh the Create workspace.",
    );
  }, [toast, workspaceError]);

  const derivedData = useMemo(
    () => buildSystemCatalogDerivedData(workspace.catalog),
    [workspace.catalog],
  );

  const eligibleTileIds = useMemo(
    () => new Set(workspace.baseTiles.map((tile) => tile.id)),
    [workspace.baseTiles],
  );

  const orphanTileSets = useMemo(() => getOrphanTileSets(workspace.catalog), [workspace.catalog]);
  const rootCollections = useMemo(() => getRootCollections(workspace.catalog), [workspace.catalog]);

  useEffect(() => {
    if (!workspace.baseTiles.some((tile) => tile.id === baseTileId)) {
      setBaseTileId(workspace.baseTiles[0]?.id ?? null);
    }
  }, [baseTileId, workspace.baseTiles]);

  useEffect(() => {
    if (!layers.some((layer) => layer.id === selectedLayerId)) {
      setSelectedLayerId(layers.at(-1)?.id ?? null);
    }
  }, [layers, selectedLayerId]);

  useEffect(() => {
    const validOverlayIds = new Set(workspace.overlays.map((overlay) => overlay.id));
    setSelectedOverlayQuantities((current) => {
      const next = Object.fromEntries(
        Object.entries(current).filter(
          ([overlayId, quantity]) => validOverlayIds.has(overlayId) && quantity > 0,
        ),
      );
      return Object.keys(next).length === Object.keys(current).length ? current : next;
    });
  }, [workspace.overlays]);

  useEffect(() => {
    if (!activeTileSetId) {
      return;
    }
    setExpandedTreeKeys((current) => {
      const key = `tile-set:${activeTileSetId}`;
      if (current.has(key)) {
        return current;
      }
      const next = new Set(current);
      next.add(key);
      return next;
    });
  }, [activeTileSetId]);

  const baseTile = useMemo(
    () => workspace.baseTiles.find((tile) => tile.id === baseTileId) ?? null,
    [baseTileId, workspace.baseTiles],
  );
  const selectedLayer = useMemo(
    () => layers.find((layer) => layer.id === selectedLayerId) ?? null,
    [layers, selectedLayerId],
  );
  const selectedOverlayEntries = useMemo(() => {
    const overlayById = new Map(workspace.overlays.map((overlay) => [overlay.id, overlay] as const));
    return Object.entries(selectedOverlayQuantities)
      .map(([overlayId, quantity]) => {
        const overlay = overlayById.get(overlayId);
        if (!overlay || quantity <= 0) {
          return null;
        }
        return { overlay, quantity };
      })
      .filter(
        (entry): entry is { overlay: AdminCreateOverlayAsset; quantity: number } => Boolean(entry),
      )
      .sort((left, right) => left.overlay.filename.localeCompare(right.overlay.filename));
  }, [selectedOverlayQuantities, workspace.overlays]);
  const selectedOverlays = useMemo(
    () =>
      selectedOverlayEntries.flatMap(({ overlay, quantity }) =>
        Array.from({ length: quantity }, () => overlay),
      ),
    [selectedOverlayEntries],
  );
  const selectedOverlayCount = useMemo(
    () => selectedOverlayEntries.reduce((sum, entry) => sum + entry.quantity, 0),
    [selectedOverlayEntries],
  );

  const panelTiles = useMemo(() => {
    if (!activeTileSetId) {
      return [];
    }
    const raw = derivedData.tilesByTileSetId.get(activeTileSetId) ?? [];
    return raw.filter((tile) => eligibleTileIds.has(tile.id));
  }, [activeTileSetId, derivedData.tilesByTileSetId, eligibleTileIds]);

  const filteredOverlays = useMemo(() => {
    const query = overlaySearch.trim().toLowerCase();
    if (!query) {
      return workspace.overlays;
    }
    return workspace.overlays.filter((overlay) =>
      `${overlay.filename} ${overlay.groupLabel}`.toLowerCase().includes(query),
    );
  }, [overlaySearch, workspace.overlays]);

  const reloadWorkspace = useCallback(async () => {
    setWorkspaceOverride(null);
    const result = await refetchWorkspace();
    if (result.error) {
      throw result.error;
    }
    return result.data ?? initialWorkspace;
  }, [initialWorkspace, refetchWorkspace]);

  const updateLayer = useCallback((layerId: string, updater: (layer: AdminCreateLayer) => AdminCreateLayer) => {
    setLayers((current) => current.map((layer) => (layer.id === layerId ? updater(layer) : layer)));
  }, []);

  function handleToggleTreeKey(key: string) {
    setExpandedTreeKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function handleSelectRoot() {
    setBrowseMode("tree");
    setBrowseCollectionId(null);
    setCatalogSelection({ kind: "root" });
    setActiveTileSetId(null);
  }

  function handleSelectTaxonomy(id: string) {
    setBrowseMode("tree");
    setBrowseCollectionId(null);
    setCatalogSelection({ kind: "taxonomy", id });
    setActiveTileSetId(null);
  }

  function handleSelectTileSet(id: string) {
    setBrowseMode("tree");
    setBrowseCollectionId(null);
    setCatalogSelection({ kind: "tile-set", id });
    setActiveTileSetId(id);
  }

  function handleSelectTileFromTree(tile: TileRecord, tileSetId: string) {
    setCatalogSelection({ kind: "tile-set", id: tileSetId });
    setActiveTileSetId(tileSetId);
    if (eligibleTileIds.has(tile.id)) {
      setBaseTileId(tile.id);
      setError(null);
    } else {
      setError("That tile cannot be used as a base (disabled set, archived, or non-system).");
    }
  }

  function handleSelectPanelTile(tile: TileRecord) {
    if (eligibleTileIds.has(tile.id)) {
      setBaseTileId(tile.id);
      setError(null);
    }
  }

  useEffect(() => {
    function handlePointerMove(event: PointerEvent) {
      const drag = dragRef.current;
      if (!drag) {
        return;
      }
      if (drag.kind === "move") {
        updateLayer(drag.layerId, (layer) => ({
          ...layer,
          x: drag.originX + (event.clientX - drag.startClientX),
          y: drag.originY + (event.clientY - drag.startClientY),
        }));
        return;
      }
      const currentDistance = Math.max(
        1,
        Math.hypot(event.clientX - drag.centerClientX, event.clientY - drag.centerClientY),
      );
      updateLayer(drag.layerId, (layer) => ({
        ...layer,
        scale: clampAdminCreateScale((drag.originScale * currentDistance) / drag.startDistance),
      }));
    }

    function handlePointerUp() {
      dragRef.current = null;
    }

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [updateLayer]);

  function toggleOverlaySelection(overlayId: string) {
    setSelectedOverlayQuantities((current) => {
      if (current[overlayId]) {
        const { [overlayId]: _removed, ...rest } = current;
        return rest;
      }
      return {
        ...current,
        [overlayId]: 1,
      };
    });
    setError(null);
  }

  function incrementOverlayQuantity(overlayId: string) {
    setSelectedOverlayQuantities((current) => ({
      ...current,
      [overlayId]: (current[overlayId] ?? 0) + 1,
    }));
    setError(null);
  }

  function decrementOverlayQuantity(overlayId: string) {
    setSelectedOverlayQuantities((current) => {
      const quantity = current[overlayId] ?? 0;
      if (quantity <= 1) {
        const { [overlayId]: _removed, ...rest } = current;
        return rest;
      }
      return {
        ...current,
        [overlayId]: quantity - 1,
      };
    });
    setError(null);
  }

  function addSelectedOverlays() {
    const transition = prepareAdminCreateComposeTransition({
      layers,
      selectedOverlays,
    });
    setLayers(transition.layers);
    setSelectedLayerId(transition.selectedLayerId);
    setSelectedOverlayQuantities({});
    setStatus(
      transition.addedOverlayCount === 1
        ? "Added 1 overlay."
        : `Added ${transition.addedOverlayCount} overlays.`,
    );
    setError(null);
  }

  function handleStartMove(event: React.PointerEvent<HTMLButtonElement>, layer: AdminCreateLayer) {
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = {
      kind: "move",
      layerId: layer.id,
      startClientX: event.clientX,
      startClientY: event.clientY,
      originX: layer.x,
      originY: layer.y,
    };
    setSelectedLayerId(layer.id);
  }

  function handleStartScale(event: React.PointerEvent<HTMLSpanElement>, layer: AdminCreateLayer) {
    event.preventDefault();
    event.stopPropagation();
    const preview = previewRef.current;
    if (!preview) {
      return;
    }
    const bounds = preview.getBoundingClientRect();
    const centerClientX = bounds.left + ADMIN_CREATE_CANVAS_WIDTH / 2 + layer.x;
    const centerClientY = bounds.top + ADMIN_CREATE_CANVAS_HEIGHT / 2 + layer.y;
    dragRef.current = {
      kind: "scale",
      layerId: layer.id,
      originScale: layer.scale,
      centerClientX,
      centerClientY,
      startDistance: Math.max(1, Math.hypot(event.clientX - centerClientX, event.clientY - centerClientY)),
    };
    setSelectedLayerId(layer.id);
  }

  async function handleSave() {
    if (!baseTile) {
      setError("Choose a base tile before saving.");
      return;
    }
    setIsSaving(true);
    setError(null);
    setStatus("Rendering tile...");
    try {
      const dataUrl = await exportAdminCreateCompositionToDataUrl({
        baseTileUrl: baseTile.url,
        layers,
      });
      const imageBase64 = dataUrl.split(",")[1];
      if (!imageBase64) {
        throw new Error("Tile export did not return image data.");
      }

      setStatus("Saving tile to the shared Created library...");
      const response = await apiFetch("/api/admin/create", {
        method: "POST",
        body: JSON.stringify({
          imageBase64,
          filename,
        }),
      });
      const payload = (await response.json()) as {
        error?: string;
        filename?: string;
        tileId?: string;
      };
      if (!response.ok) {
        throw new Error(payload.error ?? "Failed to save the created tile.");
      }
      const refreshed = await reloadWorkspace();
      if (payload.tileId && refreshed.baseTiles.some((tile) => tile.id === payload.tileId)) {
        setBaseTileId(payload.tileId);
      }
      if (payload.filename) {
        setFilename(stripPngExtension(payload.filename));
      }
      setStatus(`Saved ${payload.filename ?? "tile"} to ${refreshed.createdTileSetTitle}.`);
    } catch (saveError) {
      reportAdminCreateError("save-created-tile", saveError, {
        baseTileId: baseTile.id,
        filename,
        overlayCount: layers.length,
      });
      setError(saveError instanceof Error ? saveError.message : "Failed to save the created tile.");
      setStatus(null);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteCreatedTile(tile: AdminCreateCreatedTile) {
    setPendingDeleteId(tile.id);
    setError(null);
    setStatus(`Scheduling ${tile.filename} for deletion...`);
    try {
      const response = await apiFetch("/api/admin/create", {
        method: "DELETE",
        body: JSON.stringify({ tileId: tile.id }),
      });
      const payload = (await response.json()) as {
        error?: string;
      };
      if (!response.ok) {
        throw new Error(payload.error ?? "Failed to delete the created tile.");
      }
      await reloadWorkspace();
      setStatus(`${tile.filename} moved into scheduled deletion.`);
    } catch (deleteError) {
      reportAdminCreateError("delete-created-tile", deleteError, {
        tileId: tile.id,
        filename: tile.filename,
      });
      setError(deleteError instanceof Error ? deleteError.message : "Failed to delete the created tile.");
      setStatus(null);
    } finally {
      setPendingDeleteId(null);
    }
  }

  const stubTaxonomyDrag = useCallback(
    (node: TaxonomyNodeRecord): SystemCatalogDragPayload => ({ kind: "taxonomy-node", id: node.id }),
    [],
  );
  const stubTileSetDrag = useCallback(
    (tileSetId: string, sourceTaxonomyId: string | null): SystemCatalogDragPayload => ({
      kind: "tile-set",
      id: tileSetId,
      sourceTaxonomyId,
    }),
    [],
  );
  const stubTileDrag = useCallback(
    (tile: TileRecord, sourceTileSetId: string): SystemCatalogDragPayload => ({
      kind: "tile",
      id: tile.id,
      sourceTileSetId,
    }),
    [],
  );

  return (
    <div className="space-y-6">
      <SectionCard
        title="1 · Choose a base tile"
        description="Browse the same taxonomy tree as System Catalog, or use collections and orphans. Tiles that are not available as a base are hidden from the tile grid."
      >
        <div className="grid gap-6 lg:grid-cols-[minmax(280px,380px)_minmax(0,1fr)]">
          <div className="space-y-4">
            <button
              type="button"
              onClick={handleSelectRoot}
              className={[
                "w-full rounded-xl px-3 py-2 text-left text-sm transition",
                catalogSelection.kind === "root" && browseMode === "tree"
                  ? "bg-[#fff2d9] text-neutral-950"
                  : "bg-neutral-950/[0.035] text-neutral-700 hover:bg-[#fff2d9] hover:text-neutral-950",
              ].join(" ")}
            >
              Top level
            </button>

            <div className="max-h-[min(55vh,520px)] overflow-y-auto pr-1">
              <CatalogNavTree
                selection={catalogSelection}
                taxonomyMixedEntriesByParentId={derivedData.taxonomyMixedEntriesByParentId}
                tilesByTileSetId={derivedData.tilesByTileSetId}
                taxonomyPreviewUrlById={derivedData.taxonomyPreviewUrlById}
                tileSetPreviewUrlById={derivedData.tileSetPreviewUrlById}
                expandedKeys={expandedTreeKeys}
                onToggleExpanded={handleToggleTreeKey}
                onSelectTaxonomy={handleSelectTaxonomy}
                onSelectTileSet={handleSelectTileSet}
                getTaxonomyDragPayload={stubTaxonomyDrag}
                getTileSetDragPayload={stubTileSetDrag}
                getTileDragPayload={stubTileDrag}
                onDropOnTaxonomy={noopDropOnTaxonomy}
                onDropOnTileSet={noopDropOnTileSet}
                disableDrag
                selectedTileId={baseTileId}
                onSelectTile={handleSelectTileFromTree}
              />
            </div>

            <div className="border-t-[3px] border-neutral-950/12 pt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-neutral-600">
                Orphans
              </p>
              <button
                type="button"
                onClick={() => {
                  setBrowseMode("orphans");
                  setBrowseCollectionId(null);
                  setCatalogSelection({ kind: "root" });
                  setActiveTileSetId(null);
                }}
                className={[
                  "mb-2 w-full rounded-xl px-3 py-2 text-left text-sm transition",
                  browseMode === "orphans"
                    ? "bg-[#fff2d9] text-[#5d2f26]"
                    : "bg-neutral-950/[0.035] text-neutral-700 hover:bg-[#fff2d9] hover:text-neutral-950",
                ].join(" ")}
              >
                Orphan tile sets ({orphanTileSets.length})
              </button>
              {browseMode === "orphans" ? (
                <div className="max-h-40 space-y-1 overflow-y-auto">
                  {orphanTileSets.length > 0 ? (
                    orphanTileSets.map((ts) => (
                      <button
                        key={ts.id}
                        type="button"
                        onClick={() => {
                          setActiveTileSetId(ts.id);
                          setCatalogSelection({ kind: "tile-set", id: ts.id });
                        }}
                        className={[
                          "w-full truncate rounded-lg px-2 py-1.5 text-left text-xs transition",
                          activeTileSetId === ts.id
                            ? "bg-[#fff2d9] text-neutral-950"
                            : "text-neutral-700 hover:bg-[#fff2d9] hover:text-neutral-950",
                        ].join(" ")}
                      >
                        {ts.title}
                      </button>
                    ))
                  ) : (
                    <p className="text-xs text-neutral-600">No orphan tile sets.</p>
                  )}
                </div>
              ) : null}
            </div>

            <div className="border-t-[3px] border-neutral-950/12 pt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-neutral-600">
                Collections
              </p>
              <div className="max-h-36 space-y-1 overflow-y-auto">
                {rootCollections.map((col) => (
                  <button
                    key={col.id}
                    type="button"
                    onClick={() => {
                      setBrowseMode("collection");
                      setBrowseCollectionId(col.id);
                      setCatalogSelection({ kind: "root" });
                      setActiveTileSetId(null);
                    }}
                    className={[
                      "w-full truncate rounded-lg px-2 py-1.5 text-left text-xs transition",
                      browseMode === "collection" && browseCollectionId === col.id
                        ? "bg-[#fff2d9] text-neutral-950"
                        : "text-neutral-700 hover:bg-[#fff2d9] hover:text-neutral-950",
                    ].join(" ")}
                  >
                    {col.title}
                  </button>
                ))}
              </div>
              {browseMode === "collection" && browseCollectionId ? (
                <div className="mt-2 max-h-40 space-y-1 overflow-y-auto border-t-[3px] border-neutral-950/12 pt-2">
                  {getCollectionTileSets(workspace.catalog, browseCollectionId).map((ts) => (
                    <button
                      key={ts.id}
                      type="button"
                      onClick={() => {
                        setActiveTileSetId(ts.id);
                        setCatalogSelection({ kind: "tile-set", id: ts.id });
                      }}
                      className={[
                        "w-full truncate rounded-lg px-2 py-1.5 text-left text-xs transition",
                        activeTileSetId === ts.id
                          ? "bg-[#fff2d9] text-neutral-950"
                          : "text-neutral-700 hover:bg-[#fff2d9] hover:text-neutral-950",
                      ].join(" ")}
                    >
                      {ts.title}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex min-h-[min(72vh,560px)] flex-col rounded-2xl border border-neutral-950/15 bg-neutral-950/[0.035] p-5">
            <div>
              <h3 className="text-sm font-semibold text-neutral-950">Tiles in this set</h3>
              <p className="mt-1 text-xs text-neutral-500">
                Select a tile set in the tree (or orphans / collections). Only catalog base tiles appear here.
              </p>
            </div>
            <div className="mt-4 flex-1 overflow-y-auto">
              {activeTileSetId && panelTiles.length > 0 ? (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(248px,1fr))] gap-5">
                  {panelTiles.map((tile) => {
                    const url = getTileRecordPreviewUrl(tile);
                    const selected = tile.id === baseTileId;
                    return (
                      <button
                        key={tile.id}
                        type="button"
                        onClick={() => handleSelectPanelTile(tile)}
                        className={[
                          "overflow-hidden rounded-2xl border text-left transition",
                          selected
                            ? "border-[#3e664d] ring-2 ring-[#3e664d]/35"
                            : "border-neutral-950 hover:border-neutral-950/70",
                        ].join(" ")}
                      >
                        {url ? (
                          <img
                            src={url}
                            alt={tile.filename}
                            className="aspect-square w-full bg-neutral-950/6 object-cover"
                            draggable={false}
                          />
                        ) : (
                          <div className="flex aspect-square items-center justify-center bg-neutral-950/6 text-base text-neutral-500">
                            No preview
                          </div>
                        )}
                        <span className="block truncate px-3 py-2.5 text-base text-neutral-700">
                          {tile.filename}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <EmptyGridState message="Pick a tile set to see eligible base tiles here." />
              )}
            </div>
            {baseTile ? (
              <div className="mt-4 flex items-center gap-3 border-t-[3px] border-neutral-950/12 pt-4">
                <img
                  src={baseTile.url}
                  alt=""
                  className="h-14 w-14 shrink-0 rounded-lg border border-neutral-950/20 object-cover"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-neutral-950">{baseTile.filename}</p>
                  <p className="truncate text-xs text-neutral-600">Selected base</p>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="2 · Choose overlays"
        description="Pick one or more overlays to add to the composition below. Hover a thumbnail for a larger preview."
      >
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <input
              value={overlaySearch}
              onChange={(event) => setOverlaySearch(event.target.value)}
              placeholder="Filter overlays"
              className="min-w-[200px] flex-1 rounded-xl border border-neutral-950/20 bg-white/60 px-4 py-3 text-sm text-neutral-950 outline-none transition focus:border-neutral-950/40"
            />
            <span className="text-xs text-neutral-600">
              {layers.length} in composition below
            </span>
          </div>
          <div
            className="relative max-h-[min(70vh,640px)] overflow-y-auto rounded-2xl border-[3px] border-neutral-950 bg-[#fffaf1] p-2 shadow-[6px_6px_0_rgba(19,19,19,0.1)]"
            onMouseLeave={() => setHoverPreview(null)}
          >
            {filteredOverlays.length > 0 ? (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(48px,1fr))] gap-1">
                {filteredOverlays.map((overlay) => (
                  <button
                    key={overlay.id}
                    type="button"
                    onClick={() => toggleOverlaySelection(overlay.id)}
                    onMouseEnter={(event) => {
                      setHoverPreview({
                        url: overlay.url,
                        filename: overlay.filename,
                        clientX: event.clientX,
                        clientY: event.clientY,
                      });
                    }}
                    onMouseMove={(event) => {
                      setHoverPreview((current) =>
                        current
                          ? {
                              ...current,
                              clientX: event.clientX,
                              clientY: event.clientY,
                            }
                          : null,
                      );
                    }}
                    onMouseLeave={() => setHoverPreview(null)}
                    className={[
                      "relative aspect-square overflow-hidden rounded-md border bg-neutral-950/6 transition",
                      selectedOverlayQuantities[overlay.id]
                        ? "border-[#3e664d] ring-2 ring-[#3e664d]/35"
                        : "border-neutral-950 hover:border-[#3e664d]/50 hover:ring-1 hover:ring-[#3e664d]/30",
                    ].join(" ")}
                    title={overlay.filename}
                    aria-pressed={Boolean(selectedOverlayQuantities[overlay.id])}
                  >
                    <img
                      src={overlay.url}
                      alt=""
                      className="h-full w-full object-cover"
                      draggable={false}
                    />
                    {selectedOverlayQuantities[overlay.id] ? (
                      <span className="absolute bottom-1 right-1 rounded-full border border-neutral-950 bg-[#3e664d] px-1.5 py-0.5 text-[10px] font-semibold text-[#fff7ec] shadow-sm">
                        x{selectedOverlayQuantities[overlay.id]}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            ) : (
              <EmptyGridState message="No overlays match this filter." />
            )}
          </div>
          <div className="rounded-2xl border border-neutral-950/15 bg-neutral-950/[0.035] px-4 py-3">
            {selectedOverlayEntries.length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-neutral-950">
                      {selectedOverlayCount} overlay{selectedOverlayCount === 1 ? "" : "s"} selected
                    </p>
                    <p className="truncate text-xs text-neutral-600">
                      Adjust how many copies of each selected overlay to add.
                    </p>
                  </div>
                  <ActionButton onClick={addSelectedOverlays}>Add selected to composition</ActionButton>
                </div>
                <div className="space-y-2">
                  {selectedOverlayEntries.map(({ overlay, quantity }) => (
                    <div
                      key={overlay.id}
                      className="flex items-center justify-between gap-3 rounded-xl border border-neutral-950/15 bg-neutral-950/[0.035] px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm text-neutral-950">{overlay.filename}</p>
                        <p className="truncate text-xs text-neutral-600">{overlay.groupLabel}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => decrementOverlayQuantity(overlay.id)}
                          className="h-8 w-8 rounded-lg border-2 border-neutral-950 bg-[#fff2d9] text-sm text-neutral-800 transition hover:bg-[#ffe7bc]"
                          aria-label={`Remove one ${overlay.filename}`}
                        >
                          -
                        </button>
                        <span className="min-w-8 text-center text-sm font-medium text-neutral-950">{quantity}</span>
                        <button
                          type="button"
                          onClick={() => incrementOverlayQuantity(overlay.id)}
                          className="h-8 w-8 rounded-lg border-2 border-neutral-950 bg-[#fff2d9] text-sm text-neutral-800 transition hover:bg-[#ffe7bc]"
                          aria-label={`Add one more ${overlay.filename}`}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-neutral-600">Select overlays to add them to the composition below.</p>
            )}
          </div>
          {hoverPreview ? (
            <div
              className="pointer-events-none fixed z-100 rounded-xl border-[3px] border-neutral-950 bg-[#fffaf1] p-2 shadow-[10px_10px_0_rgba(19,19,19,0.14)]"
              style={{
                left: `${Math.max(
                  8,
                  Math.min(
                    hoverPreview.clientX + 14,
                    (typeof window !== "undefined" ? window.innerWidth : 1200) - 216,
                  ),
                )}px`,
                top: `${Math.max(
                  8,
                  Math.min(
                    hoverPreview.clientY + 14,
                    (typeof window !== "undefined" ? window.innerHeight : 800) - 216,
                  ),
                )}px`,
              }}
            >
              <img
                src={hoverPreview.url}
                alt=""
                className="max-h-52 max-w-52 rounded-lg object-contain"
              />
              <p className="mt-1 max-w-52 truncate text-center text-[10px] text-neutral-700">
                {hoverPreview.filename}
              </p>
            </div>
          ) : null}
        </div>
      </SectionCard>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
        <SectionCard
          title="3 · Compose and save"
          description="Position overlays on the base tile, then save into the shared Created library."
        >
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-3">
              <label className="min-w-[220px] flex-1">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-neutral-600">
                  Filename
                </span>
                <input
                  value={filename}
                  onChange={(event) => setFilename(event.target.value)}
                  placeholder="Created Tile"
                  className="w-full rounded-xl border border-neutral-950/20 bg-white/60 px-4 py-3 text-sm text-neutral-950 outline-none transition focus:border-neutral-950/40"
                />
              </label>
              <div className="pt-6">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!baseTile || isSaving}
                  className="rounded-xl border-[3px] border-neutral-950 bg-[#3e664d] px-5 py-3 text-sm font-medium text-[#fff7ec] shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] transition hover:bg-[#23443a] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {isSaving ? "Saving..." : "Save Tile"}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-neutral-950/15 bg-[#f7f3eb]/50 p-4">
              <div
                ref={previewRef}
                className="relative overflow-hidden rounded-[28px] border-[3px] border-neutral-950 bg-[#fffaf1] shadow-[12px_12px_0_rgba(19,19,19,0.14)]"
                style={{
                  width: `${ADMIN_CREATE_CANVAS_WIDTH}px`,
                  height: `${ADMIN_CREATE_CANVAS_HEIGHT}px`,
                }}
                onClick={() => setSelectedLayerId(null)}
              >
                {baseTile ? (
                  <img
                    src={baseTile.url}
                    alt={baseTile.filename}
                    draggable={false}
                    className="absolute inset-0 h-full w-full select-none object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-sm text-neutral-500">
                    Pick a base tile above.
                  </div>
                )}
                {layers.map((layer, index) => {
                  const selected = layer.id === selectedLayerId;
                  return (
                    <button
                      key={layer.id}
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedLayerId(layer.id);
                      }}
                      onPointerDown={(event) => handleStartMove(event, layer)}
                      className={[
                        "absolute inset-0 cursor-move bg-transparent",
                        selected ? "outline-2 outline-[#3e664d]/70" : "",
                      ].join(" ")}
                      style={{
                        zIndex: index + 1,
                        opacity: layer.opacity,
                        transform: `translate(${layer.x}px, ${layer.y}px) scale(${layer.scale})`,
                        transformOrigin: "center center",
                      }}
                    >
                      <img
                        src={layer.url}
                        alt={layer.filename}
                        draggable={false}
                        className="absolute inset-0 h-full w-full select-none object-contain"
                      />
                      {selected ? (
                        <span
                          onPointerDown={(event) => handleStartScale(event, layer)}
                          className="absolute bottom-3 right-3 h-4 w-4 rounded-full border border-neutral-950 bg-[#3e664d] shadow-[0_0_0_3px_rgba(62,102,77,0.35)]"
                        />
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-neutral-950">Layers</h3>
                <span className="text-xs text-neutral-600">
                  {layers.length} layer{layers.length === 1 ? "" : "s"}
                </span>
              </div>
              {layers.length > 0 ? (
                <div className="space-y-2">
                  {layers
                    .map((layer, index) => ({ layer, index }))
                    .reverse()
                    .map(({ layer, index }) => {
                      const actualIndex = layers.length - 1 - index;
                      const selected = layer.id === selectedLayerId;
                      return (
                        <button
                          key={layer.id}
                          type="button"
                          onClick={() => setSelectedLayerId(layer.id)}
                          className={[
                            "flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left transition",
                            selected
                              ? "border-[#3e664d]/50 bg-[#3e664d]/10"
                              : "border-neutral-950/15 bg-neutral-950/[0.035] hover:bg-[#fff2d9]",
                          ].join(" ")}
                        >
                          <span className="min-w-0">
                            <span className="block truncate text-sm text-neutral-900">{layer.filename}</span>
                            <span className="block text-xs text-neutral-600">
                              x {Math.round(layer.x)}, y {Math.round(layer.y)}, scale{" "}
                              {layer.scale.toFixed(2)}
                            </span>
                          </span>
                          <span className="text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                            {actualIndex === layers.length - 1 ? "Top" : "Layer"}
                          </span>
                        </button>
                      );
                    })}
                </div>
              ) : (
                <EmptyGridState message="Add overlays in the section above to start composing." />
              )}
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title="Layer inspector"
          description="Fine-tune the selected overlay."
        >
          {selectedLayer ? (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-neutral-900">{selectedLayer.filename}</p>
                <p className="text-xs text-neutral-600">Selected overlay</p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label>
                  <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-neutral-600">
                    X
                  </span>
                  <input
                    type="number"
                    value={Math.round(selectedLayer.x)}
                    onChange={(event) =>
                      updateLayer(selectedLayer.id, (layer) => ({
                        ...layer,
                        x: Number(event.target.value) || 0,
                      }))
                    }
                    className="w-full rounded-xl border border-neutral-950/20 bg-white/60 px-4 py-3 text-sm text-neutral-950 outline-none transition focus:border-neutral-950/40"
                  />
                </label>
                <label>
                  <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-neutral-600">
                    Y
                  </span>
                  <input
                    type="number"
                    value={Math.round(selectedLayer.y)}
                    onChange={(event) =>
                      updateLayer(selectedLayer.id, (layer) => ({
                        ...layer,
                        y: Number(event.target.value) || 0,
                      }))
                    }
                    className="w-full rounded-xl border border-neutral-950/20 bg-white/60 px-4 py-3 text-sm text-neutral-950 outline-none transition focus:border-neutral-950/40"
                  />
                </label>
                <label>
                  <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-neutral-600">
                    Scale
                  </span>
                  <input
                    type="number"
                    min={0.2}
                    max={4}
                    step={0.05}
                    value={selectedLayer.scale}
                    onChange={(event) =>
                      updateLayer(selectedLayer.id, (layer) => ({
                        ...layer,
                        scale: clampAdminCreateScale(Number(event.target.value) || 1),
                      }))
                    }
                    className="w-full rounded-xl border border-neutral-950/20 bg-white/60 px-4 py-3 text-sm text-neutral-950 outline-none transition focus:border-neutral-950/40"
                  />
                </label>
                <label>
                  <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-neutral-600">
                    Opacity
                  </span>
                  <input
                    type="number"
                    min={0}
                    max={1}
                    step={0.05}
                    value={selectedLayer.opacity}
                    onChange={(event) =>
                      updateLayer(selectedLayer.id, (layer) => ({
                        ...layer,
                        opacity: Math.min(1, Math.max(0, Number(event.target.value) || 0)),
                      }))
                    }
                    className="w-full rounded-xl border border-neutral-950/20 bg-white/60 px-4 py-3 text-sm text-neutral-950 outline-none transition focus:border-neutral-950/40"
                  />
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                <ActionButton
                  onClick={() => {
                    const duplicate = duplicateAdminCreateLayer(selectedLayer);
                    setLayers((current) => [...current, duplicate]);
                    setSelectedLayerId(duplicate.id);
                  }}
                >
                  Duplicate
                </ActionButton>
                <ActionButton
                  onClick={() => {
                    setLayers((current) => moveLayerForward(current, selectedLayer.id));
                  }}
                  disabled={layers.at(-1)?.id === selectedLayer.id}
                >
                  Bring forward
                </ActionButton>
                <ActionButton
                  onClick={() => {
                    setLayers((current) => moveLayerBackward(current, selectedLayer.id));
                  }}
                  disabled={layers[0]?.id === selectedLayer.id}
                >
                  Send back
                </ActionButton>
                <ActionButton
                  tone="destructive"
                  onClick={() => {
                    setLayers((current) => current.filter((layer) => layer.id !== selectedLayer.id));
                  }}
                >
                  Remove
                </ActionButton>
              </div>
            </div>
          ) : (
            <EmptyGridState message="Select a layer from the list or canvas." />
          )}
        </SectionCard>
      </div>

      <SectionCard
        title="Created tiles"
        description={`Tiles saved here land in ${workspace.createdTileSetTitle}. Deleting moves them into scheduled deletion.`}
      >
        {workspace.createdTiles.length > 0 ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
            {workspace.createdTiles.map((tile) => (
              <div
                key={tile.id}
                className="overflow-hidden rounded-2xl border border-neutral-950/15 bg-neutral-950/[0.035] shadow-[8px_8px_0_rgba(19,19,19,0.12)]"
              >
                <img
                  src={tile.url}
                  alt={tile.filename}
                  className="aspect-square w-full bg-neutral-950/4 object-cover"
                />
                <div className="space-y-3 p-4">
                  <div>
                    <div className="truncate text-sm font-medium text-neutral-900">{tile.filename}</div>
                    <div className="truncate font-mono text-[10px] text-neutral-500">{tile.id}</div>
                  </div>
                  <div className="flex gap-2">
                    <ActionButton
                      onClick={() => {
                        setBaseTileId(tile.id);
                      }}
                    >
                      Use as base
                    </ActionButton>
                    <ActionButton
                      tone="destructive"
                      disabled={pendingDeleteId === tile.id}
                      onClick={() => handleDeleteCreatedTile(tile)}
                    >
                      {pendingDeleteId === tile.id ? "Deleting..." : "Delete"}
                    </ActionButton>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyGridState message="Save a composition to see it here and in Orphans." />
        )}
      </SectionCard>
    </div>
  );
}
